import { Box, Button, Flex, HStack, Text, VStack } from '@gazin/ecommerce-ui'
import { useRouter } from 'next/router'
import { BsGrid1X2Fill } from 'react-icons/bs'
import { FaBars } from 'react-icons/fa'
import { ProductVerticalWithAnalytics } from 'src/components/productVertical'
import { ProductHorizontalWithAnalytics } from 'src/components/produtctHorizontal/product-horizontal-with-analytics'
import {
  Product,
  Suggestion,
} from 'src/services/linx-api/dtos/search-products-response'
import { useProductShowcases } from 'src/contexto/hooks/useProductShowcases'
import { LinxShowcaseType } from 'src/contexto/types/Linx'
import { mapProductToAnalyticsProductLinx } from 'src/components/productVertical/utils'
import { Dispatch, SetStateAction } from 'react'
import { makePageUrl } from 'src/util/url'
import Link from 'next/link'
import dynamic from 'next/dynamic'

const ProductSectionSearch = dynamic(
  () =>
    import('src/components/ProductSectionSearch').then(
      (module) => module.ProductSectionSearch
    ),
  { ssr: false }
)

export type ProductListMobileProps = {
  configPaginas: {
    currentPage: number
    firstPage: number
    lastPage: number
    nextPage: number
  }
  isCategoryPage?: boolean
  isGrid: boolean
  mapLinxProductToProductVertical: (product: Product) => any
  pagina: number
  parametros: { ordenacao: string }
  produtos?: Product[]
  resultsSize?: number
  setIsGrid: Dispatch<SetStateAction<boolean>>
  suggestedSearches?: Suggestion[]
}

export const ProductListMobile = ({
  configPaginas,
  isCategoryPage,
  isGrid,
  mapLinxProductToProductVertical,
  pagina,
  produtos,
  resultsSize,
  setIsGrid,
  suggestedSearches,
}: ProductListMobileProps) => {
  const route = useRouter()
  const indiceFinal = resultsSize && Math.min(pagina * 18, resultsSize)

  const { showcaseTop, showcaseMiddle, showcaseBottom } = useProductShowcases(
    isCategoryPage ? LinxShowcaseType.CATEGORY : LinxShowcaseType.SEARCH,
    isCategoryPage
      ? route.query && route.query.categoria && route.query.categoria[0]
      : undefined
  )

  function handleClickSeeMore() {
    const { nextPage } = configPaginas

    route.push(
      { pathname: route.pathname, query: { ...route.query, pagina: nextPage } },
      undefined,
      { shallow: true }
    )
  }

  return (
    <Box width="100%">
      <Flex flexDir="column">
        {showcaseTop.length > 0 &&
          showcaseTop?.map((showcase) => (
            <ProductSectionSearch
              key={showcase.id}
              title={showcase.title}
              subtitle={showcase.subtitle}
              products={showcase.displays[0]?.recommendations}
            />
          ))}
      </Flex>
      <HStack
        width="100%"
        justifyContent="space-between"
        alignItems="center"
        height="2rem"
        marginY="0.5rem"
      >
        <Text fontSize="0.875rem" fontWeight={700} as="span" color="#7C8096">
          {resultsSize}{' '}
          <Text as="span" fontWeight={400} fontSize="0.875rem" color="#7C8096">
            resultados encontrados
          </Text>
        </Text>
        <HStack>
          <BsGrid1X2Fill
            color="#0D71F0"
            size={20}
            opacity={!isGrid ? 0.3 : 1}
            onClick={() => setIsGrid(true)}
          />
          <FaBars
            color="#0D71F0"
            size={22}
            style={{ marginLeft: '.625rem' }}
            opacity={isGrid ? 0.3 : 1}
            onClick={() => setIsGrid(false)}
          />
        </HStack>
      </HStack>

      {isGrid ? (
        <Box
          display="grid"
          gap="1.25rem 0.1875rem"
          marginLeft={0}
          marginRight={0}
          gridTemplateColumns={{
            base: 'repeat(auto-fill, minmax(8.9375rem, 1fr))',
            md: 'repeat(auto-fill, minmax(14rem, 1fr))',
          }}
          overflow="hidden"
          width="100%"
        >
          {produtos?.map((product) => {
            return (
              <ProductVerticalWithAnalytics
                key={product.id}
                product={mapLinxProductToProductVertical(product)}
                analyticsProduct={mapProductToAnalyticsProductLinx(product)}
              />
            )
          })}
        </Box>
      ) : (
        <Box display="grid" gap="1rem 0.1875rem" overflow="hidden" width="100%">
          {produtos?.map((product) => {
            return (
              <ProductHorizontalWithAnalytics
                key={product.id}
                product={mapLinxProductToProductVertical(product)}
                analyticsProduct={mapProductToAnalyticsProductLinx(product)}
              />
            )
          })}
        </Box>
      )}

      <VStack marginY="1.5rem" spacing={2}>
        <Text color="#7C8096" fontSize="0.875rem" as="span">
          {indiceFinal} de {resultsSize} resultados encontrados
        </Text>
        {pagina < configPaginas.lastPage && (
          <>
            <Link
              href={`${makePageUrl(configPaginas.nextPage, route)}`}
              shallow={true}
              hidden
            />
            <Button
              bgColor="#0D71F0"
              color="white"
              borderRadius=".25rem"
              width="100%"
              _hover={{ bgColor: 'none' }}
              onClick={handleClickSeeMore}
            >
              Ver mais
            </Button>
          </>
        )}
      </VStack>

      <Flex flexDir="column">
        {showcaseMiddle.length > 0 &&
          showcaseMiddle?.map((showcase) => (
            <ProductSectionSearch
              key={showcase.id}
              title={showcase.name}
              subtitle={showcase.title}
              products={showcase.displays[0]?.recommendations}
            />
          ))}

        {showcaseBottom.length > 0 &&
          showcaseBottom?.map((showcase) => (
            <ProductSectionSearch
              key={showcase.id}
              title={showcase.title}
              subtitle={showcase.subtitle}
              products={showcase.displays[0]?.recommendations}
            />
          ))}
      </Flex>

      {!!suggestedSearches?.length && (
        <Box marginY="1.5rem">
          <Text color="#646981" fontWeight={600} as="span" fontSize="1rem">
            Pesquisas rápidas
          </Text>
          <Flex
            justifyContent="flex-start"
            overflowX="scroll"
            sx={{
              '::-webkit-scrollbar': {
                display: 'none',
              },
            }}
            width="100%"
          >
            {suggestedSearches.map((quer, index) => (
              <Box
                key={index}
                border=".0625rem solid #7C8096"
                paddingX=".75rem"
                paddingY=".5rem"
                borderRadius=".25rem"
                marginRight="1rem"
                marginTop="1rem"
                color="#7C8096"
                fontSize=".875rem"
                width="100%"
              >
                <Text
                  width="100%"
                  as="span"
                  color="#7C8096"
                  cursor="pointer"
                  whiteSpace="nowrap"
                  onClick={() => {
                    route.push({
                      pathname: '/busca/[...busca]',
                      query: { busca: quer.query },
                    })
                  }}
                >
                  {quer.query}
                </Text>
              </Box>
            ))}
          </Flex>
        </Box>
      )}
    </Box>
  )
}
